import { useEffect, type ReactElement } from 'react';
import { Outlet, createRootRoute, useRouterState } from '@tanstack/react-router';
import { applyTheme, applyTransparency } from '@selflevel/ui';
import { useCuentaVinculada, useOnboarded, useSettings } from '../store/hooks';
import { useAcceso } from '../store/acceso';
import { useMarcaDesplazado } from '../ui/desplazado';
import { BarraInferior, Riel } from '../ui/nav';
import { Acontecimientos } from '../features/acontecimientos';
import { AvisoVersion } from '../features/aviso-version';
import { VigiaFoco } from '../features/foco-vigia';
import { Alta } from '../features/alta';
import { Acceso } from '../features/acceso';
import { CodigoRecuperacion } from '../features/codigo-recuperacion';
import { PantallaDeCarga } from '../ui/carga';

/**
 * EL ARMAZÓN.
 *
 * Una sola estructura para los dos tamaños, y el CSS decide cuál de las dos
 * navegaciones existe. No hay `useMediaQuery`, no hay estado de «modo
 * escritorio» y no se monta ni se desmonta nada al redimensionar: las dos
 * navegaciones están siempre en el árbol y una de las dos está en `display:
 * none`. Cuesta unos cientos de bytes de DOM y ahorra la clase entera de fallos
 * en que la app se queda pensando que es un móvil porque arrancó estrecha.
 */
function Layout(): ReactElement {
  const settings = useSettings();
  const onboarded = useOnboarded();
  const cuenta = useCuentaVinculada();
  const accediendo = useAcceso((s) => s.accediendo);
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // El tema y la transparencia salen de los ajustes del usuario. Se aplican en
  // un efecto y no durante el render: escribir en el DOM mientras se renderiza
  // es lo que produce el parpadeo de tema al navegar.
  useEffect(() => {
    applyTheme(settings.theme);
    applyTransparency(settings.transparency);
  }, [settings.theme, settings.transparency]);

  // La cabecera de cada pantalla flota arriba del todo y se vuelve cristal al bajar.
  useMarcaDesplazado(pathname);

  // `undefined` = todavía leyendo IndexedDB. Ni el alta ni la app: enseñar el alta
  // durante medio segundo a alguien que ya la hizo es peor que esperar. Se espera
  // con la pantalla de carga, no con el lienzo vacío, que en el iPhone es negro.
  if (onboarded === undefined || cuenta === undefined) return <PantallaDeCarga />;

  /**
   * LA PUERTA (spec «cuenta obligatoria»): sin cuenta vinculada en este dispositivo
   * no se entra. Mientras la pantalla de acceso trabaja sigue en pantalla aunque la
   * cuenta ya esté vinculada, para no abrir la app a mitad de «Bajando lo tuyo».
   */
  if (cuenta === null || accediendo) {
    return (
      <>
        <Acceso />
        <CodigoRecuperacion />
      </>
    );
  }

  if (!onboarded) {
    return (
      <>
        <Alta />
        <CodigoRecuperacion />
        <Acontecimientos />
      </>
    );
  }

  // Sincronizar es una tarea con teclado y una sola salida, la flecha de volver
  // (paso 9): sin barra inferior que el teclado empuje y sin riel que la estreche.
  const conNavegacion = pathname !== '/cuenta';

  return (
    <div className="shell">
      {conNavegacion ? <Riel pathname={pathname} /> : null}
      <div className="shell__lienzo">
        <Outlet />
      </div>
      {conNavegacion ? <BarraInferior pathname={pathname} /> : null}
      <Acontecimientos />
      <AvisoVersion />
      <VigiaFoco />
      <CodigoRecuperacion />
    </div>
  );
}

export const rootRoute = createRootRoute({ component: Layout });
